import type { MirrorConnectedMessageWriteRequest } from "../../supabase/functions/_shared/mirrorConnectedMessageWriteIntent";
import type { MirrorConnectedTaskProposalRequest } from "../../supabase/functions/_shared/mirrorConnectedTaskWriteIntent";
import type { FinanceProposalArgs } from "../../supabase/functions/_shared/mirrorFinanceActions";
import type { BrainNoteProposalArgs, BrainHandoffArgs } from "../../supabase/functions/_shared/brainIntent";
import type { LanguageClassProposalArgs, LanguageTutorHandoffArgs } from "../../supabase/functions/_shared/languageClassWriteIntent";
import type { LanguageProposalArgs } from "../../supabase/functions/_shared/mirrorLanguageActions";
import type { FitnessCuratedPlanProposalArgs } from "../../supabase/functions/_shared/mirrorFitnessCuratedPlans";
import type { NutritionGoalsProposalArgs } from "../../supabase/functions/_shared/mirrorNutritionGoalsActions";
import type { NutritionHydrationProposalArgs } from "../../supabase/functions/_shared/mirrorNutritionHydrationActions";
import type { NutritionMealProposalArgs } from "../../supabase/functions/_shared/mirrorNutritionMealActions";
import type { FitnessWorkoutFinishArgs } from "../../supabase/functions/_shared/fitnessWorkoutFinishPreviewDomain";
import type { FitnessSavedPlanPrepareArgs } from "../../supabase/functions/_shared/fitnessSavedPlanPrepareDomain";
import type { FitnessRestSkipArgs } from "../../supabase/functions/_shared/fitnessRestSkipDomain";
import type { FitnessCurrentSetCompletionArgs } from "../../supabase/functions/_shared/fitnessCurrentSetCompletionDomain";
import type { FitnessRuntimeActionArgs } from "../../supabase/functions/_shared/fitnessRuntimeDomain";
import type { FitnessDraftRestoreArgs } from "../../supabase/functions/_shared/fitnessDraftRestoreDomain";
import type { FitnessSetupStartArgs } from "../../supabase/functions/_shared/fitnessSetupStartDomain";
import type { FitnessCurrentSetEditArgs } from "../../supabase/functions/_shared/fitnessCurrentSetEditDomain";
import type { FitnessDraftDiscardArgs } from "../../supabase/functions/_shared/fitnessDraftDiscardDomain";
import type { FitnessProfilePatch } from "../../supabase/functions/_shared/fitnessActionDomain";
import type { ActionDefinition } from "../../supabase/functions/_shared/personalActionDomain";
// Client-side mirror types.
// Must stay in lock-step with supabase/functions/_shared/mirror-tools.ts.
// Client code imports from here; the edge function imports from the _shared
// version (Deno cannot reach src/ at runtime).

export type AgendaEventTag = "Focus" | "Meeting" | "Personal";

// 21/05/26: "task" removed from NoteType. The Mirror's `create_task` tool
// now writes to the dedicated `tasks` table (Agenda module), not to
// notes(type='task'). NoteType is reserved for thought capture only.
export type NoteType = "idea" | "insight";

// Legacy structured args — kept solely so MirrorActionChip can still render
// proposals persisted in mirror_messages BEFORE PR-2 Option B shipped.
// New code paths must NOT emit this shape.
export interface AgendaEventArgs {
  title: string;
  start_at: string;
  end_at?: string;
  tag: AgendaEventTag;
  notes?: string;
  reminder_minutes?: number | null;
}

export interface ReminderProposalPreferences {
  checkin_missed_enabled?:boolean; teach_mirror_stale_enabled?:boolean; sleep_missed_enabled?:boolean;
  goals_stale_enabled?:boolean; mirror_silent_enabled?:boolean;
  timezone?:string|null; quiet_hours_start?:string|null; quiet_hours_end?:string|null;
}
export interface ProposeReminderActionArgs {
  action:'create'|'update'|'delete'|'set_enabled'; reminder_id?:string;
  category?:'hydration'|'movement'|'motivation'|'sleep'|'custom';
  message?:{key?:'hydration'|'movement'|'motivation'|'sleep';text?:string}; enabled?:boolean;
  schedule_type?:'fixed_time'|'times_per_day'|'every_n_hours'|'days_of_week';
  schedule_config?:{time?:string;times?:string[];hours?:number;days?:number[]};
}
export interface ProposeReminderPreferencesActionArgs {preferences:ReminderProposalPreferences}

export interface ProposeLocalAgendaActionArgs {
  action: "create" | "update" | "delete" | "promote_task";
  event_id?: string; task_id?: string; title?: string; tag?: "Focus" | "Meeting" | "Personal";
  start_at?: string; end_at?: string; notes?: string | null; location?: string | null;
  all_day_start?: string | null; all_day_end?: string | null; reminders?: number[];
}

export interface ProposeAgendaEventArgs {
  raw_text: string;
}

// Nutrition voice-capture (spine flow, mirrors propose_agenda_event): Sonnet
// signals "the latest message logs a meal" and passes the phrase verbatim. The
// client resolves macros via nutrition-extract and opens NutritionLogModal.
export interface ProposeNutritionLogArgs {
  raw_text: string;
}

// Focus + Sleep voice-capture (spine flow, same shape as the above): Sonnet
// signals intent only; the client resolves structure via focus-extract /
// sleep-extract and opens the module's confirm modal.
export interface ProposeCheckinActionArgs { action:'save_today'|'create_past'|'update'|'delete'; checkin_day?:string; checkin_id?:string; fields?:{mood?:number|null;energy?:number|null;note?:string|null;sleep_quality?:'great'|'okay'|'poor'|null;sleep_hours?:number|null;intentions?:('work'|'family'|'health'|'learning'|'rest'|'create'|'connect')[]|null;intention_text?:string|null;gratitude?:string|null;breathing_completed?:boolean|null;breathing_duration_seconds?:number|null} }
// checkin_id is internal server-resolved observation only; the model schema excludes it.

export interface ProposeFocusActionArgs { action:'create'|'update'|'delete'; focus_session_id?:string; task?:string; duration_minutes?:number; mode?:string|null; completed_at?:string; focus_quality?:'flow'|'bom'|'medio'|'distraido'|null }
export interface ProposeFocusTimerActionArgs { action:'start'|'pause'|'resume'|'reset'; task?:string; duration_minutes?:25|45|90 }

export interface ProposeFocusLogArgs {
  raw_text: string;
}

export interface ProposeSleepActionArgs {
  action: "create" | "update" | "delete" | "save_preferences";
  sleep_id?: string;
  fields?: { sleep_date?: string; bedtime?: string; wakeup?: string; quality?: "amazing" | "good" | "fair" | "poor" | "terrible" | null; notes?: string[] | null; note_text?: string | null };
  preferences?: { sleep_target_hours?: string | null; sleep_bedtime_reminder?: boolean | null; sleep_bedtime_reminder_time?: string | null; sleep_wakeup_reminder?: boolean | null; sleep_wakeup_reminder_time?: string | null };
}

export interface ProposeSleepLogArgs {
  raw_text: string;
}

// Habits voice-capture (spine flow, same shape): Sonnet signals "the user just
// did a habit" and passes the phrase verbatim. The client resolves a canonical
// habit NAME via habit-extract, then HabitConfirmModal matches it to an existing
// habit (or auto-creates one) before completing it.
export interface ProposeHabitLogArgs {
  raw_text: string;
}

// Workout voice-capture (spine flow, same shape): Sonnet signals "the user just
// did a workout" and passes the phrase verbatim. The client resolves a name +
// optional duration via workout-extract and opens WorkoutLogModal.
export interface ProposeWorkoutLogArgs {
  raw_text: string;
}

// Journal/Diário voice-capture (spine flow, same shape): Sonnet signals "the
// latest message is a diary entry"; the client resolves content + mode + optional
// mood via journal-extract and opens JournalLogModal.
export interface ProposeJournalLogArgs {
  raw_text: string;
}

// Finance voice-capture (spine flow, same shape): Sonnet signals "the user just
// spent or received money"; the client resolves {type, amount, category, date}
// via finance-extract and opens FinanceLogModal.
export interface ProposeFinanceLogArgs {
  raw_text: string;
}

// Body-measurement voice-capture (spine flow, same shape): Sonnet signals "the
// user stated their own measurements"; the client resolves the numbers via
// body-extract and opens BodyLogModal.
export interface ProposeBodyLogArgs {
  raw_text: string;
}

// Deliverable generation (Espelho Executor F1, same spine shape): Sonnet
// signals "the user asked the Espelho to PRODUCE a written artifact" and
// passes the phrase verbatim. The chip confirm invokes deliverable-generate
// (edge) — the write happens SERVER-side into mirror_deliverables.
export interface ProposeDeliverableArgs {
  raw_text: string;
}

export interface ProposeCalendarActionArgs {
  /** Server-normalized current creation request; never a write confirmation. */
  raw_text?: string;
  action: "search" | "create" | "update" | "delete";
  provider?: "auto" | "google_calendar" | "outlook" | "apple_calendar";
  query?: string; event_id?: string; title?: string; start?: string; end?: string;
  all_day?: boolean; time_zone?: string; location?: string; description?: string;
  native_recurrence?:{frequency:'daily'|'weekly'|'monthly'|'yearly';interval:number;count:number};
}

export interface ProposeContactActionArgs {
  action: "search" | "create" | "update" | "delete";
  provider?: "auto" | "google_contacts" | "microsoft_contacts";
  query?: string; item_id?: string;
  given_name?: string; family_name?: string; email?: string; phone?: string;
}

export interface ProposeEmailActionArgs {
  action: "search" | "draft" | "send" | "reply" | "organize";
  provider?: "auto" | "gmail" | "outlook_mail";
  query?: string;
  message_id?: string;
  operation?: "archive" | "read" | "unread" | "star" | "unstar" | "trash";
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body_text?: string;
}

export interface ProposePeopleTaskActionArgs {
  kind: "contact" | "task";
  action: "search" | "create" | "update" | "complete" | "reopen" | "delete";
  provider?: "auto" | "google_contacts" | "microsoft_contacts" | "apple_contacts" | "google_tasks" | "microsoft_todo" | "apple_reminders";
  query?: string;
  list_id?: string;
  item_id?: string;
  title?: string;
  email?: string;
  phone?: string;
  notes?: string;
  due_date?: string | null;
}

export interface ProposeFileActionArgs {
  /** Only server-derived values with this current literal source open a new Mirror review. */
  raw_text?: string;
  action: "search" | "create_text" | "update_text" | "upload_file";
  provider?: "auto" | "google_drive" | "onedrive" | "notion" | "dropbox";
  query?: string;
  name?: string;
  content_text?: string;
}

export interface TaskArgs {
  title: string;
  notes?: string;
}
export interface CreateHabitArgs { name: string; emoji?: string; frequency?: "daily" | "weekdays" | "weekends" | "custom"; color?: "cyan" | "amber" | "rose" | "violet" | "emerald" | null; custom_days?: number[] | null; sort_order?: number | null; is_active?: boolean | null }
export interface ProposeHabitActionArgs extends Partial<CreateHabitArgs> { action: "create" | "update" | "delete" | "complete" | "undo" | "create_complete"; habit_id?: string }

export interface ProposeNoteActionArgs {
  action: "create" | "update" | "delete" | "send_to_mirror";
  note_id?: string; content?: string; type?: "idea" | "insight";
}
export interface ProposeLocalTaskActionArgs {
  action: "create" | "update" | "delete" | "complete" | "reopen";
  task_id?: string; title?: string; priority?: "Normal" | "Important" | "Critical";
  due_date?: string | null; time_label?: string | null;
}

export interface NoteArgs {
  content: string;
  type?: NoteType;
}

export interface ProposeGoalActionArgs {
  action: "create" | "update" | "delete" | "set_progress" | "complete" | "reopen" | "add_milestone" | "update_milestone" | "delete_milestone" | "set_milestone";
  goal_id?: string; milestone_id?: string; query?: string; title?: string; description?: string | null;
  category?: "Personal" | "Professional" | "Health" | "Financial" | "Mental" | "Relationships" | "Creative" | "Learning";
  due_date?: string | null; progress_override?: number | null; text?: string; is_done?: boolean; milestones?: { text: string }[];
}
export interface ProposeGoalArgs { raw_text: string }

export interface ProposePersonalActionArgs { title: string; steps: ActionDefinition[] }

export interface ProposeBodyHealthActionArgs {
  action: "create" | "update" | "delete";
  resource: "body" | "health";
  record_id?: string;
  fields?: Record<string, unknown>;
}

export interface ProposeFitnessActionArgs { action: "save_profile" | "activate_plan" | "deactivate_plan"; plan_id?: string; patch?: FitnessProfilePatch }

export type ActionProposal =
  | {tool:"propose_finance_action";action_id:string;args:FinanceProposalArgs;preview:string}
  | {tool:"propose_brain_note_action";action_id:string;args:BrainNoteProposalArgs;preview:string}
  | {tool:"prepare_brain_handoff";action_id:string;args:BrainHandoffArgs;preview:string}
  | {tool:"propose_language_class_action";action_id:string;args:LanguageClassProposalArgs;preview:string}
  | {tool:"prepare_language_tutor_handoff";action_id:string;args:LanguageTutorHandoffArgs;preview:string}
  | {tool:"propose_language_action";action_id:string;args:LanguageProposalArgs;preview:string}
  | {tool:"propose_fitness_curated_plan";action_id:string;args:FitnessCuratedPlanProposalArgs;preview:string}
  | {tool:"propose_nutrition_goals_action";action_id:string;args:NutritionGoalsProposalArgs;preview:string}
  | {tool:"propose_nutrition_hydration_action";action_id:string;args:NutritionHydrationProposalArgs;preview:string}
  | {tool:"propose_nutrition_meal_action";action_id:string;args:NutritionMealProposalArgs;preview:string}
  | {tool:"propose_fitness_workout_finish";action_id:string;args:FitnessWorkoutFinishArgs;preview:string}
  | { tool: "propose_fitness_saved_plan_prepare"; action_id: string; args: FitnessSavedPlanPrepareArgs; preview: string }
  | { tool: "propose_fitness_local_draft_discard"; action_id: string; args: FitnessDraftDiscardArgs; preview: string }
  | { tool: "propose_fitness_runtime_action"; action_id: string; args: FitnessRuntimeActionArgs; preview: string }
  | { tool: "propose_fitness_draft_restore"; action_id: string; args: FitnessDraftRestoreArgs; preview: string }
  | { tool: "propose_fitness_rest_skip"; action_id: string; args: FitnessRestSkipArgs; preview: string }
  | { tool: "propose_fitness_current_set_completion"; action_id: string; args: FitnessCurrentSetCompletionArgs; preview: string }
  | { tool: "propose_fitness_setup_start"; action_id: string; args: FitnessSetupStartArgs; preview: string }
  | { tool: "propose_fitness_current_set_edit"; action_id: string; args: FitnessCurrentSetEditArgs; preview: string }
  | { tool: "propose_fitness_action"; action_id: string; args: ProposeFitnessActionArgs; preview: string }
  | {tool:"propose_body_health_action";action_id:string;args:ProposeBodyHealthActionArgs;preview:string}
  | {tool:"propose_sleep_action";action_id:string;args:ProposeSleepActionArgs;preview:string}
  | {tool:"propose_checkin_action";action_id:string;args:ProposeCheckinActionArgs;preview:string}
  | {tool:"propose_focus_action";action_id:string;args:ProposeFocusActionArgs;preview:string}
  | {tool:"propose_focus_timer_action";action_id:string;args:ProposeFocusTimerActionArgs;preview:string}
  | {tool:'propose_reminder_action';action_id:string;args:ProposeReminderActionArgs;preview:string}
  | {tool:'propose_reminder_preferences_action';action_id:string;args:ProposeReminderPreferencesActionArgs;preview:string}
  | { tool: "propose_local_agenda_action"; action_id: string; args: ProposeLocalAgendaActionArgs; preview: string }
  | { tool: "create_habit"; action_id: string; args: CreateHabitArgs; preview: string }
  | { tool: "propose_habit_action"; action_id: string; args: ProposeHabitActionArgs; preview: string }
  | { tool: "propose_note_action"; action_id: string; args: ProposeNoteActionArgs; preview: string }
  | { tool: "propose_local_task_action"; action_id: string; args: ProposeLocalTaskActionArgs; preview: string }
  | { tool: "propose_goal_action"; action_id: string; args: ProposeGoalActionArgs; preview: string }
  | { tool: "propose_goal"; action_id: string; args: ProposeGoalArgs; preview: string }
  | { tool: "propose_personal_action"; action_id: string; args: ProposePersonalActionArgs; preview: string }
  | {
      tool: "propose_agenda_event";
      action_id: string;
      args: ProposeAgendaEventArgs;
      preview: string;
    }
  | {
      tool: "create_agenda_event";
      action_id: string;
      args: AgendaEventArgs;
      preview: string;
    }
  | {
      tool: "create_task";
      action_id: string;
      args: TaskArgs;
      preview: string;
    }
  | {
      tool: "create_note";
      action_id: string;
      args: NoteArgs;
      preview: string;
    }
  | {
      tool: "propose_nutrition_log";
      action_id: string;
      args: ProposeNutritionLogArgs;
      preview: string;
    }
  | {
      tool: "propose_focus_log";
      action_id: string;
      args: ProposeFocusLogArgs;
      preview: string;
    }
  | {
      tool: "propose_sleep_log";
      action_id: string;
      args: ProposeSleepLogArgs;
      preview: string;
    }
  | {
      tool: "propose_habit_log";
      action_id: string;
      args: ProposeHabitLogArgs;
      preview: string;
    }
  | {
      tool: "propose_workout_log";
      action_id: string;
      args: ProposeWorkoutLogArgs;
      preview: string;
    }
  | {
      tool: "propose_journal_log";
      action_id: string;
      args: ProposeJournalLogArgs;
      preview: string;
    }
  | {
      tool: "propose_finance_log";
      action_id: string;
      args: ProposeFinanceLogArgs;
      preview: string;
    }
  | {
      tool: "propose_body_log";
      action_id: string;
      args: ProposeBodyLogArgs;
      preview: string;
    }
  | {
      tool: "propose_deliverable";
      action_id: string;
      args: ProposeDeliverableArgs;
      preview: string;
      /**
       * Anexos do turno, carimbados pelo SERVIDOR (F1 PR 2). O cliente lê
       * daqui e devolve no invoke do deliverable-generate — transporte puro,
       * sem manter uma contabilidade própria que possa divergir. Opcional
       * porque proposta persistida antes desta versão não tem o campo.
       */
      attachment_ids?: string[];
    }
  | { tool: "propose_calendar_action"; action_id: string; args: ProposeCalendarActionArgs; preview: string }
  | { tool: "propose_contact_action"; action_id: string; args: ProposeContactActionArgs; preview: string }
  | {
      tool: "propose_email_action";
      action_id: string;
      args: ProposeEmailActionArgs;
      preview: string;
    }
  | { tool: "propose_connected_message_action"; action_id: string; args: MirrorConnectedMessageWriteRequest; preview: string }
  | { tool: "propose_connected_task_action"; action_id: string; args: MirrorConnectedTaskProposalRequest; preview: string }
  | {
      tool: "propose_file_action";
      action_id: string;
      args: ProposeFileActionArgs;
      preview: string;
    }
  | {
      tool: "propose_people_task_action";
      action_id: string;
      args: ProposePeopleTaskActionArgs;
      preview: string;
    };

export type ActionTool = ActionProposal["tool"];

// 21/05/26: create_task now lands in the `tasks` table — same table the
// Agenda's Tarefas list reads from. Mirror-created tasks become to-dos
// without a specific time (priority 'Normal', no due_date) so they appear
// in the Agenda task list and the user can manage them there.
export const ACTION_TABLE_BY_TOOL: Record<ActionTool, "browser_fitness_runtime" | "browser_session_draft" | "daily_checkins" | "focus_timer_runs" | "agenda_events" | "notes" | "tasks" | "nutrition_logs" | "nutrition_hydration" | "nutrition_goals" | "workout_plans" | "focus_sessions" | "sleep_logs" | "habit_completions" | "workout_sessions" | "journal_entries" | "finance_transactions" | "body_measurements" | "mirror_deliverables" | "connector_action_receipts" | "personal_action_plans" | "goals" | "habits" | "mirror_reminders" | "user_whisper_preferences"> = {
  propose_nutrition_meal_action: "nutrition_logs",
  propose_nutrition_hydration_action: "nutrition_hydration",
  propose_language_action: "connector_action_receipts",
  propose_finance_action: "connector_action_receipts",
  propose_language_class_action: "connector_action_receipts",
  prepare_language_tutor_handoff: "connector_action_receipts",
  propose_brain_note_action: "notes",
  prepare_brain_handoff: "connector_action_receipts",
  propose_nutrition_goals_action: "nutrition_goals",
  propose_fitness_curated_plan: "workout_plans",
  propose_reminder_action: "mirror_reminders",
  propose_reminder_preferences_action: "user_whisper_preferences",
  propose_agenda_event: "agenda_events",
  propose_local_agenda_action: "agenda_events",
  create_agenda_event: "agenda_events",
  create_task: "tasks",
  create_note: "notes",
  // Spine flow — the write happens in the module's confirm modal, not via
  // useMirrorActions.executeProposal (which only handles the create_* chip
  // tools). Listed for ActionTool completeness + documentation.
  propose_nutrition_log: "nutrition_logs",
  propose_checkin_action: "daily_checkins",
  propose_focus_log: "focus_sessions",
  propose_focus_action: "focus_sessions",
  propose_focus_timer_action: "focus_timer_runs",
  propose_sleep_log: "sleep_logs",
  propose_sleep_action: "sleep_logs",
  // Reviewed Fitness writes use durable receipts rather than direct chip writes.
  propose_fitness_action: "connector_action_receipts",
  // Routing sentinel only: the dedicated reviewed browser path owns removal.
  propose_fitness_local_draft_discard: "browser_session_draft",
  // Routing sentinel only; current reviewed same-tab control never writes a table.
  propose_fitness_runtime_action: "browser_fitness_runtime",
  propose_fitness_draft_restore: "browser_fitness_runtime",
  propose_fitness_saved_plan_prepare: "browser_fitness_runtime",
  propose_fitness_workout_finish: "workout_sessions",
  propose_fitness_rest_skip: "browser_fitness_runtime",
  propose_fitness_current_set_completion: "browser_fitness_runtime",
  propose_fitness_setup_start: "browser_fitness_runtime",
  propose_fitness_current_set_edit: "browser_fitness_runtime",
  // Habits writes a completion row; the habit row itself may be auto-created
  // first when the spoken name matches none of the user's habits.
  propose_habit_log: "habit_completions",
  create_habit: "habits",
  propose_habit_action: "habits",
  propose_workout_log: "workout_sessions",
  propose_journal_log: "journal_entries",
  propose_finance_log: "finance_transactions",
  propose_body_log: "body_measurements",
  // Routing metadata only; the reviewed executor chooses body/health.
  propose_body_health_action: "body_measurements",
  // Espelho Executor — the write happens in the deliverable-generate edge fn
  // (service role), not via useMirrorActions.executeProposal. Listed for
  // ActionTool completeness + documentation, same as the spine tools above.
  propose_deliverable: "mirror_deliverables",
  propose_email_action: "connector_action_receipts",
  propose_calendar_action: "connector_action_receipts",
  propose_contact_action: "connector_action_receipts",
  propose_file_action: "connector_action_receipts",
  propose_connected_message_action: "connector_action_receipts",
  propose_connected_task_action: "connector_action_receipts",
  propose_people_task_action: "connector_action_receipts",
  propose_personal_action: "personal_action_plans",
  propose_goal: "goals",
  propose_goal_action: "goals",
  propose_local_task_action: "tasks",
  propose_note_action: "notes",
};
